import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO_BASE = {
  siteName: 'BuildConnect',
  siteUrl: 'https://buildconnect.be',
  defaultTitle: 'BuildConnect - Trouvez des sous-traitants BTP en Belgique, Luxembourg & France',
  defaultDescription: 'Marketplace professionnelle du bâtiment. Publiez vos chantiers et trouvez des sous-traitants qualifiés en maçonnerie, toiture, plomberie, électricité et plus. Belgique, Luxembourg, France.',
  defaultImage: '/og-image.png',
  locale: 'fr_BE',
  twitterHandle: '@buildconnect'
};

export const SEOHead = ({ title, description, path, image, type = 'website', noindex = false, jsonLd }) => {
  const fullTitle = title ? `${title} | ${SEO_BASE.siteName}` : SEO_BASE.defaultTitle;
  const desc = description || SEO_BASE.defaultDescription;
  const url = path ? `${SEO_BASE.siteUrl}${path}` : SEO_BASE.siteUrl;
  const img = image || SEO_BASE.defaultImage;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SEO_BASE.siteName} />
      <meta property="og:locale" content={SEO_BASE.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

// Organization Schema for BuildConnect
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'BuildConnect',
  legalName: 'Muller Construction SRL',
  url: 'https://buildconnect.be',
  description: 'Marketplace professionnelle du bâtiment en Belgique, Luxembourg et France',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'BE'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+32 61 46 83 32',
    email: 'contact@buildconnect.be',
    contactType: 'customer service',
    availableLanguage: ['French', 'Dutch']
  },
  sameAs: []
};

// WebSite Schema with SearchAction
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'BuildConnect',
  url: 'https://buildconnect.be',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://buildconnect.be/jobs?city={search_term_string}',
    'query-input': 'required name=search_term_string'
  }
};

// Generate JobPosting schema for a job
export const jobPostingSchema = (job) => ({
  '@context': 'https://schema.org',
  '@type': 'JobPosting',
  title: job.title,
  description: job.description,
  datePosted: job.created_at,
  validThrough: job.start_date,
  employmentType: 'CONTRACTOR',
  jobLocation: {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      addressLocality: job.city,
      addressCountry: job.country === 'Belgium' ? 'BE' : job.country === 'Luxembourg' ? 'LU' : 'FR'
    },
    ...(job.latitude && job.longitude ? {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: job.latitude,
        longitude: job.longitude
      }
    } : {})
  },
  hiringOrganization: {
    '@type': 'Organization',
    name: job.company_name
  },
  ...(job.budget_min || job.budget_max ? {
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: 'EUR',
      value: {
        '@type': 'QuantitativeValue',
        minValue: job.budget_min || 0,
        maxValue: job.budget_max || 0,
        unitText: 'PROJECT'
      }
    }
  } : {})
});

// Generate LocalBusiness schema for a contractor
export const contractorSchema = (contractor) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `https://buildconnect.be/contractors/${contractor.id}`,
  name: contractor.company_name,
  description: contractor.description || `Sous-traitant BTP à ${contractor.city || 'Belgique'}`,
  address: {
    '@type': 'PostalAddress',
    addressLocality: contractor.city,
    addressCountry: contractor.country === 'Belgium' ? 'BE' : contractor.country === 'Luxembourg' ? 'LU' : 'FR'
  },
  ...(contractor.latitude && contractor.longitude ? {
    geo: {
      '@type': 'GeoCoordinates',
      latitude: contractor.latitude,
      longitude: contractor.longitude
    }
  } : {}),
  ...(contractor.rating > 0 ? {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: contractor.rating,
      reviewCount: contractor.review_count || 1,
      bestRating: 5,
      worstRating: 1
    }
  } : {}),
  ...(contractor.vat_number ? { vatID: contractor.vat_number } : {}),
  ...(contractor.phone ? { telephone: contractor.phone } : {})
});

export default SEOHead;
